import { useCallback, useContext, useState } from 'react';

import Descriptor from '@r6digital/rapidstor-types/react/entities/Descriptor';
import Location from '@r6digital/rapidstor-types/react/entities/Location';
import UnitType from '@r6digital/rapidstor-types/react/entities/UnitType';
import Deal from '@r6digital/rapidstor-types/types/Deal';

import store from '@/store';
import {
    ReservationData,
    setDeal,
    setReservationData,
    setUnitData,
    setUpsell,
} from '@/store/bookingSlice';
import { resolveDealForUnitSwitch } from '@/journey-based-listing/components/UnitSelect/utils/dealMatching';
import { DescriptorOptions } from '@/features/rapidstor/UnitOptions';
import { JourneyContext } from '@/features/rapidstor/providers/JourneyProvider';

export interface PendingDescriptorSwitch {
    descriptor: Descriptor;
    unitType: UnitType;
    matchedDeal: Deal | null;
}

export interface UseOptionWithDealReturn {
    handleUnitTypeSwitch: (newUnitType: UnitType) => void;
    handleDescriptorSwitch: (newDescriptor: Descriptor, newUnitType: UnitType) => void;
    confirmDescriptorSwitch: () => void;
    cancelDescriptorSwitch: () => void;
    pendingDescriptorSwitch: PendingDescriptorSwitch | null;
}

export function useOptionWithDeal(
    currentDescriptor: Descriptor,
    currentDealId: string | null,
    descriptors: Descriptor[],
    location: Location,
): UseOptionWithDealReturn {
    const {
        setSelectedUnitType,
        setSelectedDescriptor,
        reservationData,
    } = useContext(JourneyContext);

    const [pendingDescriptorSwitch, setPendingDescriptorSwitch] = useState<PendingDescriptorSwitch | null>(null);

    const handleUnitTypeSwitch = useCallback((newUnitType: UnitType) => {
        const { deal, dealObject, matched } = resolveDealForUnitSwitch(newUnitType, currentDealId);

        if (!matched && location.rapidstor.hideStandardPrices && newUnitType.deals?.length) {
            const firstDeal = newUnitType.deals[0];
            store.dispatch(setDeal({ deal: firstDeal._id as string, dealObject: firstDeal }));
        } else {
            store.dispatch(setDeal({ deal, dealObject: dealObject ?? undefined }));
        }
        setSelectedUnitType(newUnitType);
    }, [currentDealId, location.rapidstor.hideStandardPrices, setSelectedUnitType]);

    const handleDescriptorSwitch = useCallback((newDescriptor: Descriptor, newUnitType: UnitType) => {
        const { dealObject, matched } = resolveDealForUnitSwitch(newUnitType, currentDealId);

        let resolvedDeal = dealObject;
        if (!matched && location.rapidstor.hideStandardPrices && newUnitType.deals?.length) {
            const firstDeal = newUnitType.deals[0];
            resolvedDeal = firstDeal;
        }

        setPendingDescriptorSwitch({
            descriptor: newDescriptor,
            unitType: newUnitType,
            matchedDeal: resolvedDeal,
        });
    }, [currentDealId, location.rapidstor.hideStandardPrices]);

    const confirmDescriptorSwitch = useCallback(() => {
        if (!pendingDescriptorSwitch) return;

        const { descriptor: newDescriptor, unitType: newUnitType, matchedDeal } = pendingDescriptorSwitch;

        // Update unit data
        store.dispatch(setUnitData({
            location,
            unitType: newUnitType,
            descriptor: newDescriptor,
            descriptors,
        }));

        // Build reservation data with resolved deal
        const reservationStoreData: ReservationData = {
            ...reservationData,
            unitType: newUnitType,
            descriptor: newDescriptor,
            deal: matchedDeal?._id as string ?? '',
            dealObject: matchedDeal ?? undefined,
            coupon: null,
        };

        if (reservationData?.token) {
            reservationStoreData.token = reservationData.token;
        }

        store.dispatch(setReservationData(reservationStoreData));

        // Recompute upsell for new descriptor
        const options = DescriptorOptions(newDescriptor, descriptors, location);
        const upSellDescriptor = options.nextSizeUp(descriptors, newDescriptor.area);
        store.dispatch(setUpsell(upSellDescriptor));

        // Update context
        setSelectedUnitType(newUnitType);
        setSelectedDescriptor(newDescriptor);

        setPendingDescriptorSwitch(null);
    }, [pendingDescriptorSwitch, descriptors, location, reservationData, setSelectedUnitType, setSelectedDescriptor]);

    const cancelDescriptorSwitch = useCallback(() => {
        setPendingDescriptorSwitch(null);
    }, []);

    return {
        handleUnitTypeSwitch,
        handleDescriptorSwitch,
        confirmDescriptorSwitch,
        cancelDescriptorSwitch,
        pendingDescriptorSwitch,
    };
}
